import {
  SegmentAttributeDatetime,
  SegmentAttributeFilterRender,
  SegmentAttributeGroup,
  SegmentAttributeOptions,
  SegmentAttributeRange,
  SegmentAttributeTransactional,
  SegmentDetailAttributeDate,
  SegmentDetailAttributeOptions,
  SegmentDetailAttributeRange,
  SegmentDetailParsedAttributeGroup,
  SegmentFilterOperator,
  SelectedAttribute
} from '../models/segments';
import _ from 'lodash';

const isInputAttribute = (filterOperator: SegmentFilterOperator, selectedAttribute: SelectedAttribute) => {
  return (
    !(filterOperator === SegmentFilterOperator.Is || filterOperator === SegmentFilterOperator['Is Not']) ||
    'searchText' in selectedAttribute
  );
};

export const evalAttributeFilterRenderType = (
  attribute: SegmentAttributeOptions | SegmentAttributeRange | SegmentAttributeDatetime | SegmentAttributeTransactional,
  selectedAttribute: SelectedAttribute
): SegmentAttributeFilterRender | undefined => {
  if (attribute) {
    if (attribute.filterType === 'options') {
      if (isInputAttribute(selectedAttribute.filterOperator, selectedAttribute)) {
        return SegmentAttributeFilterRender.Input;
      } else if ('options' in selectedAttribute) {
        return SegmentAttributeFilterRender.Options;
      }
    }

    if (attribute.filterType === 'range' && 'range' in selectedAttribute) {
      return SegmentAttributeFilterRender.Range;
    }

    if (
      attribute.filterType === 'datetime' &&
      ('dateRange' in selectedAttribute || 'numberOfDays' in selectedAttribute)
    ) {
      return SegmentAttributeFilterRender.Datetime;
    }

    return SegmentAttributeFilterRender.Transactional;
  }

  return undefined;
};

export const CreateDefaultTransactionalAttributeQuery = (id: number, name: string, columnName: string) => {
  return {
    isError: false,
    error: undefined,
    isLoading: false,
    data: {
      id,
      name,
      columnName,
      populatedPercentage: undefined,
      filterType: undefined,
      isTransactional: true as true,
      filters: {
        where: 'all'
      }
    }
  };
};

/**
 * Find the first nested attribute (not group) and return the id for use as part of a list key for an attribute group
 */
export function findBaseAttributeId(
  predicate:
    | SegmentAttributeGroup
    | SelectedAttribute
    | SegmentDetailParsedAttributeGroup
    | SegmentDetailAttributeOptions
    | SegmentDetailAttributeRange
    | SegmentDetailAttributeDate
) {
  if ('attributeId' in predicate) {
    return predicate.attributeId;
  }
  return findBaseAttributeId(predicate.predicates[0]);
}

export const transactionalAttributeIdOffset: number = 1000000;

export const mapTransactionalAttribute = (attribute) => {
  const attributeId = attribute['attributeId'] || attribute['id'];
  const newId = !attribute.isTransactional ? attributeId : attributeId + transactionalAttributeIdOffset;
  return {
    ...attribute,
    id: newId,
    attributeId: newId,
    filterOperator: !attribute.isTransactional ? attribute.filterOperator : SegmentFilterOperator.Where
  };
};

export const unmapTransactionalAttribute = (attribute) => {
  const attributeId = attribute['attributeId'] || attribute['id'];
  const newId = !attribute.isTransactional ? attributeId : attributeId - transactionalAttributeIdOffset;
  if (attribute.isTransactional) {
    attribute = _.omit(attribute, ['options', 'dateRange', 'range', 'numberOfDays']);
  }
  return {
    ...attribute,
    id: newId,
    attributeId: newId
  };
};

export const mapTransactionalAttributes = (attributes) =>
  attributes?.map((attribute) => {
    if ('predicates' in attribute) {
      const listOfAttributes = mapTransactionalAttributes(attribute.predicates);
      return {
        operator: attribute.operator,
        predicates: listOfAttributes,
        customers: attribute.customers
      };
    } else {
      const attributeId = attribute['attributeId'] || attribute['id'];
      const newId = !attribute.isTransactional ? attributeId : attributeId + transactionalAttributeIdOffset;
      return {
        ...attribute,
        id: newId,
        attributeId: newId,
        filterOperator: !attribute.isTransactional ? attribute.filterOperator : SegmentFilterOperator.Where
      };
    }
  });

export const unmapTransactionalAttributes = (attributes) =>
  attributes?.map((attribute) => {
    if ('predicates' in attribute) {
      const listOfAttributes = unmapTransactionalAttributes(attribute.predicates);
      return {
        operator: attribute.operator,
        predicates: listOfAttributes,
        customers: attribute.customers
      };
    } else {
      const attributeId = attribute['attributeId'] || attribute['id'];
      const newId = !attribute.isTransactional ? attributeId : attributeId - transactionalAttributeIdOffset;
      if (attribute.isTransactional) {
        attribute = _.omit(attribute, ['options', 'dateRange', 'range', 'numberOfDays']);
      }
      return {
        ...attribute,
        id: newId,
        attributeId: newId
      };
    }
  });
